import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { auth, db } from "../Firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import "../styles/CoursePlayer.css";

/* ---------- Duration convert ---------- */
const durationToSeconds = (str) => {
  if (!str) return 0;
  const h = str.match(/(\d+)h/);
  const m = str.match(/(\d+)m/);
  const s = str.match(/(\d+)s/);

  return (h ? +h[1] : 0) * 3600 +
         (m ? +m[1] : 0) * 60 +
         (s ? +s[1] : 0);
};

const CoursePlayer = () => {
  const { id: courseId } = useParams();

  const videoRef = useRef(null);
  const watchInterval = useRef(null);
  const lastTimeRef = useRef(0);
  const localWatchRef = useRef(0);
  const savedWatchRef = useRef(0);

  const [course, setCourse] = useState(null);
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedIds, setCompletedIds] = useState([]);
  const [timingId, setTimingId] = useState(null);
  const [totalDuration, setTotalDuration] = useState(0);
  const [livePercent, setLivePercent] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ---------- LOAD COURSE ---------- */
  useEffect(() => {
    const load = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const id = `${user.uid}_${courseId}`;
      setTimingId(id);

      const courseSnap = await getDoc(doc(db, "courses", courseId));
      if (!courseSnap.exists()) return;

      const data = courseSnap.data();
      const vids = (data.videos || []).filter(v => !v.isSample);

      setCourse(data);
      setVideos(vids);
      setTotalDuration(durationToSeconds(data.duration));

      const timingRef = doc(db, "courseTiming", id);
      const timingSnap = await getDoc(timingRef);

      if (!timingSnap.exists()) {
        await setDoc(timingRef, {
          courseId,
          userId: user.uid,
          watchTime: 0,
          progressPercentage: 0,
          lastVideoIndex: 0,
          lastVideoSecond: 0,
          completedVideoIds: [],
          completed: false
        });
      } else {
        const t = timingSnap.data();

        savedWatchRef.current = t.watchTime || 0;
        setLivePercent(t.progressPercentage || 0);
        setIsCompleted((t.progressPercentage || 0) >= 100);

        setCurrentIndex(Math.min(t.lastVideoIndex || 0, vids.length - 1));
        setCompletedIds(t.completedVideoIds || []);

        setTimeout(() => {
          if (videoRef.current)
            videoRef.current.currentTime = t.lastVideoSecond || 0;
        }, 400);
      }

      setLoading(false);
    };

    load();
  }, [courseId]);

  /* ---------- TRACK WATCH ---------- */
  const handlePlay = () => {
    if (isCompleted) return;

    clearInterval(watchInterval.current);
    lastTimeRef.current = videoRef.current?.currentTime || 0;

    watchInterval.current = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;

      const current = video.currentTime;
      const diff = current - lastTimeRef.current;

      if (diff > 0 && diff < 1.2) {
        localWatchRef.current += diff;

        const totalWatched = savedWatchRef.current + localWatchRef.current;
        const percent = Math.min((totalWatched / totalDuration) * 100, 100);
        setLivePercent(Math.round(percent));
      }

      lastTimeRef.current = current;
    }, 1000);
  };

  /* ---------- SAVE WATCH ---------- */
  const saveWatchTime = async (forceEnd = false) => {
    if (!timingId || isCompleted) return;

    const video = videoRef.current;
    if (!video) return;

    if (forceEnd) {
      const remaining = video.duration - lastTimeRef.current;
      if (remaining > 0 && remaining < 5) {
        localWatchRef.current += remaining;
      }
    }

    if (localWatchRef.current <= 0) return;

    const timingRef = doc(db, "courseTiming", timingId);
    const snap = await getDoc(timingRef);
    if (!snap.exists()) return;

    let newTotal = (snap.data().watchTime || 0) + localWatchRef.current;
    let percent = Math.min(Math.round((newTotal / totalDuration) * 100), 100);

    const isLastVideo = currentIndex === videos.length - 1 && forceEnd;
    if (isLastVideo) {
      newTotal = totalDuration;
      percent = 100;
      setIsCompleted(true);
    }

    await updateDoc(timingRef, {
      watchTime: newTotal,
      progressPercentage: percent,
      lastVideoSecond: video.currentTime,
      lastVideoIndex: currentIndex,
      completed: percent >= 95
    });

    savedWatchRef.current = newTotal;
    localWatchRef.current = 0;
  };

  /* ---------- PAUSE ---------- */
  const handlePause = async () => {
    clearInterval(watchInterval.current);
    if (!isCompleted) await saveWatchTime();
  };

  /* ---------- VIDEO END ---------- */
  const handleEnd = async () => {
    clearInterval(watchInterval.current);
    if (!isCompleted) await saveWatchTime(true);

    const user = auth.currentUser;
    if (!user) return;

    const vid = videos[currentIndex];
    if (!vid) return;

    const updatedCompleted = Array.from(new Set([...completedIds, vid.id]));

    const nextIndex = Math.min(currentIndex + 1, videos.length - 1);

    await updateDoc(doc(db, "courseTiming", `${user.uid}_${courseId}`), {
      completedVideoIds: updatedCompleted,
      lastVideoSecond: 0,
      lastVideoIndex: nextIndex
    });

    setCompletedIds(updatedCompleted);
    setCurrentIndex(nextIndex);
  };

  /* ---------- AUTO SAVE ---------- */
  useEffect(() => {
    if (!timingId || isCompleted) return;
    const interval = setInterval(saveWatchTime, 5000);
    return () => clearInterval(interval);
  }, [timingId, currentIndex, isCompleted]);

  /* ---------- EXIT SAVE ---------- */
  useEffect(() => {
    if (isCompleted) return;
    const exitSave = () => saveWatchTime();
    window.addEventListener("beforeunload", exitSave);
    return () => window.removeEventListener("beforeunload", exitSave);
  }, [timingId, currentIndex, isCompleted]);

  /* ---------- UNLOCK RULE ---------- */
  const isUnlocked = (index) => {
    if (index === 0) return true;
    return completedIds.includes(videos[index - 1]?.id);
  };

  if (loading || videos.length === 0)
    return <p style={{ padding: 40 }}>Loading…</p>;

  const safeIndex = Math.min(currentIndex, videos.length - 1);
  const currentVideo = videos[safeIndex];

  return (
    <div className="course-player-page">
      <h2>{course.title}</h2>

      <div style={{ marginBottom: 10, fontWeight: 600 }}>
        {isCompleted ? "Course Completed 🎉" : `Progress: ${livePercent}%`}
      </div>

      <video
        ref={videoRef}
        key={currentVideo.id}
        controls
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnd}
        className="course-video-player"
      >
        <source src={currentVideo.url} type="video/mp4" />
      </video>

      <h3>Course Videos</h3>

      <div className="video-grid">
        {videos.map((v, i) => {
          const unlocked = isUnlocked(i);

          return (
            <div
              key={v.id}
              className={`video-card ${i === safeIndex ? "active" : ""}`}
              onClick={async () => {
                if (!unlocked) return;
                if (!isCompleted) await saveWatchTime();
                setCurrentIndex(i);
              }}
            >
              <div className="video-thumb">
                <img src={course.thumbnail} alt={v.title} loading="lazy" />
                {!unlocked && <div className="lock-overlay">🔒 Locked</div>}
                {unlocked && <div className="play-overlay">▶ Play</div>}
              </div>

              <div className="video-info">
                <strong>{i + 1}. {v.title}</strong>
                <p>{v.description || "No description"}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CoursePlayer;
